import { watch } from 'vue';
import { usePersonStore } from '~/stores/person';

/**
 * Crisp live-chat support, matching the hive-ui integration (same workspace,
 * website ID 117215d5-19e8-48b6-8ae0-e3ff96d6f5b8).
 *
 * Loads Crisp eagerly on every checkout page and prefills the buyer's email so
 * support sees who they're talking to. Checkout starts logged-out and
 * authenticates mid-funnel, so we set the email on init if it's already known
 * and watch the person store to set it the moment the buyer signs in — pushes
 * queue safely on `$crisp` whether or not the script has finished loading.
 */
export default defineNuxtPlugin(() => {
    const store = usePersonStore();

    const w = window as any;
    w.$crisp = [];
    w.$crisp.push(['safe', true]);
    w.CRISP_WEBSITE_ID = '117215d5-19e8-48b6-8ae0-e3ff96d6f5b8';

    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);

    const setEmail = (email: string | undefined) => {
        if (email) w.$crisp.push(['set', 'user:email', email]);
    };

    setEmail(store.person?.identity?.email);
    watch(
        () => store.person?.identity?.email,
        (email) => setEmail(email)
    );

    // Keyboard accessibility for the Crisp launcher (matches hive-ui): make it
    // focusable and toggle the chat with Enter/Escape once Crisp has painted.
    script.onload = () => {
        setTimeout(() => {
            const widget = document.querySelector('.crisp-client') as HTMLElement | null;
            if (!widget) return;

            widget.setAttribute('tabindex', '0');
            widget.setAttribute('role', 'button');
            widget.setAttribute('aria-label', 'Open chat support');

            widget.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    w.$crisp.push(['do', 'chat:open']);
                } else if (event.key === 'Escape') {
                    event.preventDefault();
                    w.$crisp.push(['do', 'chat:close']);
                }
            });

            widget.style.outline = 'none';
            widget.addEventListener('focus', () => {
                widget.style.boxShadow = '0 0 0 2px #3b82f6';
            });
            widget.addEventListener('blur', () => {
                widget.style.boxShadow = 'none';
            });
        }, 1000);
    };
});
